import React, { useState, useEffect, useCallback } from 'react';
import {
  ListFilter,
  UsersRound,
  ArrowDownNarrowWide,
  Scale,
  HandCoins,
  ChevronDown,
  Check,
  CircleDotDashed,
} from 'lucide-react';
import Pagination from '../../components/ui/pagination/Pagination';
import SearchInput from '../../components/ui/inputs/SearchInput';
import ClientBalancingNoteBadge from '../../components/ui/badges/ClientBalancingNoteBadge';
import CustomCheckbox from '../../components/ui/checkboxes/CustomCheckbox';
import CustomInput from '../../components/ui/inputs/CustomInput';
import { formatCurrency } from '../../utils/formatters';
import { camelizeKeys } from '../../utils/camelizeKeys';
import SortableHeader from '../../components/ui/table/SortableHeader';
import ClientAccountNumberBadge from '../../components/ui/badges/ClientAccountNumberBadge';
import CurrencyInput from '../../components/ui/inputs/CurrencyInput';
import { getStoredHeaderPlannerId } from '../../utils/headerPlannerSelection';

const FilterCard = ({ title, icon, children }) => {
  return (
    <div className="bg-blueLight p-4" style={{ borderRadius: '10px' }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-tealMedium">{icon}</span>
        <span className="text-blueDarker font-semibold text-lg">{title}</span>
      </div>
      {children}
    </div>
  );
};

const DropdownSelect = ({ value, options, onChange, width = 140 }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative" style={{ width }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white border border-blueDarker/20 rounded-xl px-4 py-2 flex items-center justify-between text-blueDarker text-base focus:outline-none"
      >
        <span className="truncate">{value}</span>
        <ChevronDown size={18} strokeWidth={2} className="text-blueDarker/40" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 w-full mt-1 bg-white border border-blueDarker/20 rounded-xl shadow-lg py-1 max-h-60 overflow-auto">
            {options.map((opt) => (
              <div
                key={opt}
                className={`px-4 py-2 cursor-pointer text-base truncate transition-colors ${
                  value === opt
                    ? 'bg-tealMedium/20 font-semibold text-blueDarker'
                    : 'hover:bg-grayExtraLight4 text-blueDarker'
                }`}
                onClick={() => {
                  onChange(opt);
                  setIsOpen(false);
                }}
              >
                {opt}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const PROFILE_OPTIONS = ['Todos', 'Conservador', 'Conservador-Moderado', 'Moderado', 'Moderado-Agressivo', 'Agressivo'];

const BALANCING_OPTIONS = ['Todos', 'Balanceado', 'Atenção', 'Desbalanceado'];

const Clients = ({ apiClients: initialApiClients, pagination: initialPagination, currentAdmin }) => {
  const [apiClients, setApiClients] = useState(initialApiClients);
  const [pagination, setPagination] = useState(initialPagination);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const [configFilters, setConfigFilters] = useState({
    plannerId:
      new URLSearchParams(window.location.search).get('planner_id') ||
      getStoredHeaderPlannerId(currentAdmin?.id) ||
      '',
    status: {
      actives: true,
      inactives: true,
    },
    profile: {
      suitability: 'Todos',
      wallet: 'Todos',
    },
    balancing: {
      investments: 'Todos',
      pension: 'Todos',
    },
    patrimony: {
      minEnabled: false,
      min: '',
      maxEnabled: false,
      max: '',
      onlyZeroed: false,
    },
  });

  const extractBool = (val) => {
    if (typeof val === 'boolean') return val;
    if (val && val.target) return val.target.checked;
    return false;
  };

  const buildQuery = useCallback((params) => {
    const parts = [];

    Object.entries(params).forEach(([k, v]) => {
      if (v === '' || v === null || typeof v === 'undefined' || v === false) return;

      if (Array.isArray(v)) {
        if (v.length === 0) return;

        v.forEach((item) => {
          parts.push(`${encodeURIComponent(k)}[]=${encodeURIComponent(item)}`);
        });

        return;
      }

      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    });

    return parts.join('&');
  }, []);

  const buildFilterParams = useCallback(() => {
    const params = {};

    const statuses = [];

    if (configFilters.status.actives) statuses.push('active');
    if (configFilters.status.inactives) statuses.push('inactive');

    if (statuses.length > 0) {
      params.statuses = statuses;
    }

    if (configFilters.profile.suitability !== 'Todos') {
      params.suitability = configFilters.profile.suitability;
    }

    if (configFilters.profile.wallet !== 'Todos') {
      params.wallet = configFilters.profile.wallet;
    }

    if (configFilters.balancing.investments !== 'Todos') {
      params.investments = configFilters.balancing.investments;
    }

    if (configFilters.balancing.pension !== 'Todos') {
      params.pension = configFilters.balancing.pension;
    }

    if (configFilters.patrimony.minEnabled && configFilters.patrimony.min) {
      params.minPatrimony = configFilters.patrimony.min;
    }

    if (configFilters.patrimony.maxEnabled && configFilters.patrimony.max) {
      params.maxPatrimony = configFilters.patrimony.max;
    }

    if (configFilters.patrimony.onlyZeroed) {
      params.onlyZeroed = true;
    }

    if (configFilters.plannerId) {
      params.planner_id = configFilters.plannerId;
    }

    return params;
  }, [configFilters]);

  const fetchData = useCallback(
    (page = 1) => {
      const filterParams = buildFilterParams();

      const query = buildQuery({
        ...filterParams,
        search,
        page,
        sort: sortBy,
        order: sortOrder,
      });

      fetch(`/app/analyst/clients?${query}`)
        .then((res) => res.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const appRoot = doc.querySelector('#app-root');

          if (!appRoot || !appRoot.dataset || !appRoot.dataset.props) return;

          const parsed = camelizeKeys(JSON.parse(appRoot.dataset.props));

          setApiClients(parsed.apiClients);
          setPagination(parsed.pagination);
        })
        .catch((err) => console.error('Erro ao buscar clientes:', err));
    },
    [buildFilterParams, buildQuery, search, sortBy, sortOrder]
  );

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleInvestmentBalancingChange = (val) => {
    setConfigFilters((prev) => ({
      ...prev,
      balancing: {
        ...prev.balancing,
        investments: val,
      },
    }));
  };

  const handlePensionBalancingChange = (val) => {
    setConfigFilters((prev) => ({
      ...prev,
      balancing: {
        ...prev.balancing,
        pension: val,
      },
    }));
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchData(1);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [configFilters, search, sortBy, sortOrder, fetchData]);

  const handlePageChange = (newPage) => {
    fetchData(newPage);
  };

  useEffect(() => {
    const updatePlannerIdFromUrl = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const newPlannerId = urlParams.get('planner_id') || '';

      setConfigFilters((prev) => {
        if (prev.plannerId !== newPlannerId) {
          return { ...prev, plannerId: newPlannerId };
        }

        return prev;
      });
    };

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(this, args);
      updatePlannerIdFromUrl();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      updatePlannerIdFromUrl();
    };

    window.addEventListener('popstate', updatePlannerIdFromUrl);

    return () => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', updatePlannerIdFromUrl);
    };
  }, []);

  return (
    <div className="p-5 h-full">
      <div className="grid grid-cols-4 gap-5 h-full">
        <div className="col-span-3">
          <div className="flex justify-between items-center mb-4">
            <span className="flex items-center gap-2">
              <UsersRound className="text-tealMedium" size={20} strokeWidth={1.5} />
              <h1 className="text-2xl font-semibold text-blueDarker">Clientes</h1>
            </span>

            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 text-blueDarker text-base">
                <ArrowDownNarrowWide size={20} strokeWidth={1.5} className="text-tealMedium" />
                <span className="font-semibold text-blueDarker">Ordenado por:</span>
                <span className="text-blueDarker">última atualização</span>
              </div>

              <div className="relative">
                <div className="w-96">
                  <SearchInput
                    name="search"
                    placeholder="Pesquisar cliente..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="px-2">
            <div
              className="grid items-center gap-4 text-blueDarker text-base font-semibold px-1 py-2"
              style={{ gridTemplateColumns: '120px 1.5fr 1fr 140px 150px 170px 170px' }}
            >
              <SortableHeader field="account" label="Conta" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />

              <SortableHeader field="name" label="Nome" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />

              <SortableHeader
                field="planner"
                label="Planejador"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader field="pl" label="PL Total" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />

              <SortableHeader
                field="wallet"
                label="Caixa disponível"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader
                field="balancing"
                label="Nota Balanceamento"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <div className="truncate text-right">&nbsp;</div>
            </div>
          </div>

          <div className="space-y-0 mt-1">
            {apiClients.map((c, idx) => {
              const isWhiteRow = idx % 2 !== 1;

              return (
                <div key={c.id} className={`${isWhiteRow ? 'bg-white' : 'bg-transparent'} rounded-xl`}>
                  <div
                    className="grid items-center gap-4 px-3 py-3"
                    style={{ gridTemplateColumns: '120px 1.5fr 1fr 140px 150px 170px 170px' }}
                  >
                    <div className="truncate">
                      <ClientAccountNumberBadge accountNumber={c.accountNumber} active={c.active} />
                    </div>

                    <div className="truncate font-semibold text-blueDarker">{c.name}</div>

                    <div className="truncate text-blueDarker/80">{c.plannerName}</div>

                    <div className="truncate text-blueDarker">{formatCurrency(c.pl)}</div>

                    <div className="truncate text-blueDarker">{formatCurrency(c.walletBalance)}</div>

                    <div className="truncate flex items-center gap-2">
                      <ClientBalancingNoteBadge
                        score={c.balancingStatus.investment.score}
                        label="INV"
                        tone={c.balancingStatus.investment.tone}
                      />

                      <ClientBalancingNoteBadge
                        score={c.balancingStatus.pension.score}
                        label="PREV"
                        tone={c.balancingStatus.pension.tone}
                      />
                    </div>

                    <div className="flex justify-end">
                      <a href={`/app/analyst/client/${c.id}/dashboard?from=operations.clients`}>
                        <button className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-semibold rounded-full px-5 pt-3 pb-2 text-xs border-r-2 border-b-2 border-tealBase">
                          MAIS INFORMAÇÕES
                        </button>
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}

            {apiClients.length === 0 ? (
              <div className="bg-white rounded-custom20 p-6 text-center text-blueDarker/70">
                Nenhum cliente encontrado.
              </div>
            ) : null}
          </div>

          <div className="mt-6 flex items-center justify-between">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />

            <div className="text-blueDarker text-base">
              {pagination.totalCount > 0 ? (
                <>
                  <span>Mostrando </span>

                  <span className="font-semibold">
                    {(pagination.currentPage - 1) * pagination.perPage + 1}-
                    {Math.min(pagination.currentPage * pagination.perPage, pagination.totalCount)}
                  </span>

                  <span> de </span>

                  <span className="font-semibold">{pagination.totalCount} resultados</span>
                </>
              ) : (
                <span className="font-semibold">0 resultados</span>
              )}
            </div>
          </div>
        </div>

        <div className="col-span-1 bg-white p-8 rounded-custom20 h-full">
          <span className="flex items-center gap-2 mb-4">
            <ListFilter className="text-tealMedium" size={20} strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold text-blueDarker">Filtros</h1>
          </span>

          <div className="space-y-4">
            <FilterCard
              title="Status:"
              icon={<CircleDotDashed size={16} strokeWidth={2} className="text-tealMedium" />}
            >
              <div className="flex items-center gap-8">
                <CustomCheckbox
                  checked={configFilters.status.actives}
                  label="Ativos"
                  labelClassName="text-base"
                  onChange={(v) =>
                    setConfigFilters((prev) => ({
                      ...prev,
                      status: { ...prev.status, actives: extractBool(v) },
                    }))
                  }
                />

                <CustomCheckbox
                  checked={configFilters.status.inactives}
                  label="Inativos"
                  labelClassName="text-base"
                  onChange={(v) =>
                    setConfigFilters((prev) => ({
                      ...prev,
                      status: { ...prev.status, inactives: extractBool(v) },
                    }))
                  }
                />
              </div>
            </FilterCard>

            <FilterCard title="Perfil:" icon={<UsersRound size={16} strokeWidth={2} className="text-tealMedium" />}>
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-blueDarker text-base">Suitability:</span>

                  <DropdownSelect
                    value={configFilters.profile.suitability}
                    options={PROFILE_OPTIONS}
                    onChange={(val) => {
                      setConfigFilters((prev) => ({
                        ...prev,
                        profile: { ...prev.profile, suitability: val },
                      }));
                    }}
                    width={150}
                  />
                </div>

                <div className="flex items-center justify-between gap-3">
                  <span className="text-blueDarker text-base">Carteira:</span>

                  <DropdownSelect
                    value={configFilters.profile.wallet}
                    options={PROFILE_OPTIONS}
                    onChange={(val) => {
                      setConfigFilters((prev) => ({
                        ...prev,
                        profile: { ...prev.profile, wallet: val },
                      }));
                    }}
                    width={150}
                  />
                </div>
              </div>
            </FilterCard>

            <FilterCard title="Balanceamento:" icon={<Scale size={16} strokeWidth={2} className="text-tealMedium" />}>
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-blueDarker text-base">Investimentos:</span>

                  <DropdownSelect
                    value={configFilters.balancing.investments}
                    options={BALANCING_OPTIONS}
                    onChange={handleInvestmentBalancingChange}
                    width={150}
                  />
                </div>

                <div className="flex items-center justify-between gap-3">
                  <span className="text-blueDarker text-base">Previdência:</span>

                  <DropdownSelect
                    value={configFilters.balancing.pension}
                    options={BALANCING_OPTIONS}
                    onChange={handlePensionBalancingChange}
                    width={150}
                  />
                </div>
              </div>
            </FilterCard>

            <FilterCard title="Patrimônio:" icon={<HandCoins size={16} strokeWidth={2} className="text-tealMedium" />}>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-3 select-none cursor-pointer mr-10">
                    <input
                      type="checkbox"
                      checked={configFilters.patrimony.minEnabled}
                      onChange={(e) =>
                        setConfigFilters((prev) => ({
                          ...prev,
                          patrimony: { ...prev.patrimony, minEnabled: e.target.checked },
                        }))
                      }
                      className="hidden"
                    />

                    <span
                      className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                        configFilters.patrimony.minEnabled
                          ? 'border-tealMedium bg-white'
                          : 'border-blueDarker/20 bg-white'
                      }`}
                    >
                      {configFilters.patrimony.minEnabled && (
                        <Check size={14} strokeWidth={2.5} className="text-tealMedium" />
                      )}
                    </span>

                    <span className="text-blueDarker text-base whitespace-nowrap">Mínimo (R$):</span>
                  </label>

                  <CurrencyInput
                    value={configFilters.patrimony.min}
                    onChange={(e) =>
                      setConfigFilters((prev) => ({
                        ...prev,
                        patrimony: { ...prev.patrimony, min: e.target.value },
                      }))
                    }
                    className="flex-1 bg-white border border-blueDarker/20 rounded-xl px-3 py-2 text-blueDarker text-base focus:outline-none focus:ring-2 focus:ring-tealMedium/30"
                    placeholder="0"
                    inputMode="numeric"
                    disabled={!configFilters.patrimony.minEnabled}
                  />
                </div>

                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-3 select-none cursor-pointer mr-10">
                    <input
                      type="checkbox"
                      checked={configFilters.patrimony.maxEnabled}
                      onChange={(e) =>
                        setConfigFilters((prev) => ({
                          ...prev,
                          patrimony: { ...prev.patrimony, maxEnabled: e.target.checked },
                        }))
                      }
                      className="hidden"
                    />

                    <span
                      className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                        configFilters.patrimony.maxEnabled
                          ? 'border-tealMedium bg-white'
                          : 'border-blueDarker/20 bg-white'
                      }`}
                    >
                      {configFilters.patrimony.maxEnabled && (
                        <Check size={14} strokeWidth={2.5} className="text-tealMedium" />
                      )}
                    </span>

                    <span className="text-blueDarker text-base whitespace-nowrap">Máximo (R$):</span>
                  </label>

                  <CustomInput
                    value={configFilters.patrimony.max}
                    onChange={(e) =>
                      setConfigFilters((prev) => ({
                        ...prev,
                        patrimony: { ...prev.patrimony, max: e.target.value },
                      }))
                    }
                    className="flex-1 bg-white border border-blueDarker/20 rounded-xl px-3 py-2 text-blueDarker text-base focus:outline-none focus:ring-2 focus:ring-tealMedium/30"
                    placeholder=""
                    inputMode="numeric"
                    disabled={!configFilters.patrimony.maxEnabled}
                  />
                </div>

                <CustomCheckbox
                  checked={configFilters.patrimony.onlyZeroed}
                  label="Apenas carteiras zeradas"
                  labelClassName="text-base"
                  onChange={(v) =>
                    setConfigFilters((prev) => ({
                      ...prev,
                      patrimony: { ...prev.patrimony, onlyZeroed: extractBool(v) },
                    }))
                  }
                />
              </div>
            </FilterCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Clients;
